import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { GlobalService } from '../../../global/services/global.service';
import { environment } from '../../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class JdcManagerService {
  constructor(
    private http: HttpClient,
    private jsonObjectFactory: JsonObjectFactoryService,
    private globalService: GlobalService,
  ) {}

  private get baseUrl(): string {
    return environment.base_url;
  }

  private postJson(url: string, params: any): Observable<any> {
    return this.http.post<any>(url, params || {}, { withCredentials: true });
  }

  getInitInfo(filterParams: any): Observable<any> {
    const clientKey = filterParams?.client?.value ?? filterParams?.client_key ?? '';
    return this.http.post<any>(`${this.baseUrl}/getInitInfoForJCD`, { clientKey }, { withCredentials: true });
  }

  getJCDKey(filterParams: any): Observable<any> {
    const params = {
      clientKey: filterParams?.client?.value ?? '',
      jurisdictionKey: filterParams?.jurisdiction?.value ?? 1,
      domainKey: filterParams?.domain?.value ?? 9,
      channelKey: filterParams?.channel?.value ?? ''
    };
    return this.postJson(`${this.baseUrl}/fetch_jcd_key`, params);
  }

  loadResultForJDC(filterParams: any): Observable<any> {
    const params = {
      clientKey: filterParams?.client?.value ?? '',
      jurisdictionKey: filterParams?.jurisdiction?.value ?? 1,
      domainKey: filterParams?.domain?.value ?? 9,
      channelKey: filterParams?.channel?.value ?? ''
    };
    return this.postJson(`${this.baseUrl}/getApplicationJCDList`, params);
  }

  getTaskForJCD(filterParams: any): Observable<any> {
    const params = {
      clientKey: filterParams?.client?.value ?? '',
      jurisdictionKey: filterParams?.jurisdiction?.value ?? 1,
      domainKey: filterParams?.domain?.value ?? 9,
      channelKey: filterParams?.channel?.value ?? ''
    };
    return this.postJson(`${this.baseUrl}/getTaskForJCD`, params);
  }

  loadResultForJDCSettings(clientKey: number | string): Observable<any> {
    return this.postJson(`${this.baseUrl}/ajaxGetJcdSettings`, { clientKey });
  }

  saveJsonAction(actionCode: string, payload: any[]): Observable<any> {
    const params = {
      jsonObj: JSON.stringify(payload || []),
      jsonSettings: '{}'
    };
    return this.jsonObjectFactory.saveJSON6(
      `${this.baseUrl}/saveJsonObject?action_code=${actionCode}`,
      params,
      this.globalService.global || {},
      {}
    );
  }

  addNewJcd(context: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/addNewJcd`, { data: JSON.stringify(context || {}) }, { withCredentials: true });
  }

  deleteJcd(jcdInfo: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/deleteJCDCom`, { data: JSON.stringify(jcdInfo || {}) }, { withCredentials: true });
  }

  getActivity(activityKey: number | string, clientKey: number | string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/getJcdActivity`, { activityKey, clientKey }, { withCredentials: true });
  }

  deleteActivity(activityKey: number | string, jcdKey: number | string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/deleteActivity`, { activityKey, jcdKey }, { withCredentials: true });
  }

  removeTask(taskKey: number | string, clientKey: number | string): Observable<any> {
    const params = {
      jsonObj: JSON.stringify([{ TASK_KEY: taskKey, CLIENT_KEY: clientKey }]),
      jsonSettings: '{}'
    };
    return this.jsonObjectFactory.saveJSON6(
      `${this.baseUrl}/saveJsonObject?action_code=ag0apb`,
      params,
      this.globalService.global || {},
      {}
    );
  }

  getTaxTypeDetails(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/getTaxTypeData`, { withCredentials: true });
  }

  saveActivity(activity: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/saveActivity`, {
      data: JSON.stringify(activity || {}),
      jcdTaRoleKeysToBeDeleted: activity?.jcdTaRoleKeysToBeDeleted || ''
    }, { withCredentials: true });
  }

  saveCopyAccess(
    mappedData: any[],
    sourceActivityKey: number | string,
    targetActivityKey: number | string,
    jcdKey: number | string,
    clientKey: number | string,
    taxYear: number | string,
    scenario: number | string
  ): Observable<any> {
    return this.postJson(`${this.baseUrl}/saveJsonObject?action_code=fwd8b9`, {
      roles_map: JSON.stringify(mappedData || []),
      src_activity_key: sourceActivityKey,
      target_activity_key: targetActivityKey,
      jcd_key: jcdKey,
      client_key: clientKey,
      tax_year: taxYear,
      scenario
    });
  }

  getActivityUsers(
    activityKey: number | string,
    jcdKey: number | string,
    clientKey: number | string,
    taxYear: number | string,
    scenario: number | string
  ): Observable<any> {
    return this.postJson(`${this.baseUrl}/loadJsonObject?action_code=sgsbng`, {
      activity_key: activityKey,
      jcd_key: jcdKey,
      client_key: clientKey,
      tax_year: taxYear,
      scenario
    });
  }

  getTaxYearForJCD(filterParams: any): Observable<any> {
    const params = {
      client_key: filterParams?.client?.value ?? '',
      jurisdiction_key: filterParams?.jurisdiction?.value ?? 1,
      channel_key: filterParams?.channel?.value ?? '',
      domain_key: filterParams?.domain?.value ?? 9
    };
    return this.http.post<any>(
      `${this.baseUrl}/loadJsonObject?action_id=307`,
      params,
      { withCredentials: true }
    );
  }

  shuffleActivity(activitiesJson: string): Observable<any> {
    return this.postJson(`${this.baseUrl}/shuffleActivity`, { data: activitiesJson });
  }

  getAllTemplates(clientKey: number | string): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/loadJsonObject?action_id=31674&client_key=${clientKey}`,
      { withCredentials: true }
    );
  }

  saveTemplateMapping(activityKey: number | string, jcdTaKey: number | string, templateRoleMap: any[]): Observable<any> {
    return this.postJson(`${this.baseUrl}/saveJsonObject?action_code=f36j6z`, {
      clob_data: JSON.stringify({
        activity_key: activityKey,
        jcd_ta_key: jcdTaKey,
        template_role_map: templateRoleMap
      })
    });
  }

  buildTaskTreePayload(tasks: any[], jcdKey: any, clientKey: any): any[] {
    const payload: any[] = [];
    const walk = (nodes: any[], parentTaskKey: any) => {
      (nodes || []).forEach((task: any, index: number) => {
        payload.push({
          TASK_PRIORITY: index + 1,
          TASK_KEY: task.task_key,
          TASK_NAME: task.task_name,
          TASK_DESC: task.task_desc,
          TASK_STATUS: task.task_status?.value ?? task.task_status ?? 'A',
          JCD_KEY: jcdKey,
          PARENT_TASK_KEY: parentTaskKey || '',
          CLIENT_KEY: clientKey
        });
        walk(task.sub_task || [], task.task_key);
      });
    };
    walk(tasks || [], '');
    return payload;
  }
}
